import {commitMutation, graphql} from 'react-relay'
import type {Environment, RecordSourceProxy, RecordSourceSelectorProxy} from 'relay-runtime'
import type {
  AddSubIssueInput,
  addSubIssueMutation,
  addSubIssueMutation$data,
} from './__generated__/addSubIssueMutation.graphql'
import {md2html} from '@github-ui/md2html'

function getSubIssuesConnection(parentRecord: NonNullable<ReturnType<RecordSourceProxy['get']>>) {
  return parentRecord.getLinkedRecord('subIssues', {first: 100}) || null
}

function getSubIssuesConnectionAlias(parentRecord: NonNullable<ReturnType<RecordSourceProxy['get']>>) {
  return parentRecord.getLinkedRecord('subIssuesConnection') || null
}

function ensureSubIssueRequiredFields(
  store: RecordSourceProxy,
  subIssueRecord: NonNullable<ReturnType<RecordSourceProxy['get']>>,
): void {
  const subIssueId = subIssueRecord.getDataID()

  // Ensure titleHTML exists (use title as fallback)
  const titleHTML = subIssueRecord.getValue('titleHTML')
  if (!titleHTML) {
    const title = subIssueRecord.getValue('title')
    if (typeof title === 'string') {
      subIssueRecord.setValue(md2html(title), 'titleHTML')
    }
  }

  // Ensure assignees connection exists with the correct arguments (first: 10)
  const assigneesConnection = subIssueRecord.getLinkedRecord('assignees', {first: 10})
  if (!assigneesConnection) {
    const placeholderAssignees = store.create(`${subIssueId}:assignees:optimistic`, 'UserConnection')
    placeholderAssignees.setLinkedRecords([], 'nodes')
    placeholderAssignees.setValue(0, 'totalCount')
    subIssueRecord.setLinkedRecord(placeholderAssignees, 'assignees', {first: 10})
  }

  // Ensure issueDependenciesSummary exists (required by SubIssueStateIcon)
  const issueDependenciesSummary = subIssueRecord.getLinkedRecord('issueDependenciesSummary')
  if (!issueDependenciesSummary) {
    const placeholderDependenciesSummary = store.create(
      `${subIssueId}:issueDependenciesSummary:optimistic`,
      'IssueDependenciesSummary',
    )
    placeholderDependenciesSummary.setValue(0, 'openBlockedByCount')
    placeholderDependenciesSummary.setValue(0, 'closedBlockedByCount')
    placeholderDependenciesSummary.setValue(0, 'openBlockingCount')
    placeholderDependenciesSummary.setValue(0, 'closedBlockingCount')
    subIssueRecord.setLinkedRecord(placeholderDependenciesSummary, 'issueDependenciesSummary')
  }

  // Ensure subIssuesSummary exists (required by SubIssuesListItem_NestedSubIssues)
  const subIssuesSummary = subIssueRecord.getLinkedRecord('subIssuesSummary')
  if (!subIssuesSummary) {
    const placeholderSubIssuesSummary = store.create(`${subIssueId}:subIssuesSummary:optimistic`, 'SubIssuesSummary')
    placeholderSubIssuesSummary.setValue(0, 'total')
    placeholderSubIssuesSummary.setValue(0, 'completed')
    placeholderSubIssuesSummary.setValue(0, 'percentCompleted')
    subIssueRecord.setLinkedRecord(placeholderSubIssuesSummary, 'subIssuesSummary')
  }

  // Ensure closedByPullRequestsReferences connection exists
  const closedByPRs = subIssueRecord.getLinkedRecord('closedByPullRequestsReferences', {
    first: 0,
    includeClosedPrs: true,
  })
  if (!closedByPRs) {
    const placeholderClosedByPRs = store.create(
      `${subIssueId}:closedByPullRequestsReferences:optimistic`,
      'IssueClosedByPullRequestsReferencesConnection',
    )
    placeholderClosedByPRs.setLinkedRecords([], 'nodes')
    placeholderClosedByPRs.setValue(0, 'totalCount')
    subIssueRecord.setLinkedRecord(placeholderClosedByPRs, 'closedByPullRequestsReferences', {
      first: 0,
      includeClosedPrs: true,
    })
  }
}

function optimisticUpdateSubIssuesConnection(store: RecordSourceProxy, input: AddSubIssueInput) {
  if (!input.subIssueId) return

  const parentRecord = store.get(input.issueId)
  if (!parentRecord) return

  const subIssueRecord = store.get(input.subIssueId)
  if (!subIssueRecord) return

  // Only update if the connection already exists
  const subIssuesConnection = getSubIssuesConnection(parentRecord)
  if (!subIssuesConnection) return

  // Ensure the sub-issue has all required fields for rendering (creates placeholders if missing)
  ensureSubIssueRequiredFields(store, subIssueRecord)

  // Check if the sub-issue is already in the connection
  const currentNodes = subIssuesConnection.getLinkedRecords('nodes') || []
  const alreadyExists = currentNodes.some(node => node?.getValue('id') === input.subIssueId)

  if (!alreadyExists) {
    // Add the sub-issue to the connection
    const newNodes = [...currentNodes, subIssueRecord]
    subIssuesConnection.setLinkedRecords(newNodes, 'nodes')

    // Update totalCount on the connection
    const currentTotalCount = subIssuesConnection.getValue('totalCount')
    if (typeof currentTotalCount === 'number') {
      subIssuesConnection.setValue(currentTotalCount + 1, 'totalCount')
    } else {
      subIssuesConnection.setValue(currentNodes.length + 1, 'totalCount')
    }
  }

  // Update subIssuesSummary on the parent if it exists
  const subIssuesSummary = parentRecord.getLinkedRecord('subIssuesSummary')
  if (subIssuesSummary) {
    const currentTotal = subIssuesSummary.getValue('total')
    if (typeof currentTotal === 'number') {
      subIssuesSummary.setValue(currentTotal + 1, 'total')
    }

    // Update completed count if the sub-issue is closed
    const isClosed = subIssueRecord.getValue('closed') === true
    if (isClosed) {
      const currentCompleted = subIssuesSummary.getValue('completed')
      if (typeof currentCompleted === 'number') {
        subIssuesSummary.setValue(currentCompleted + 1, 'completed')
      }
    }
  }

  // Update subIssuesConnection totalCount (aliased field used by useSubIssuesSummary)
  const subIssuesConnectionRecord = getSubIssuesConnectionAlias(parentRecord)
  if (subIssuesConnectionRecord) {
    const connectionTotalCount = subIssuesConnectionRecord.getValue('totalCount')
    if (typeof connectionTotalCount === 'number') {
      subIssuesConnectionRecord.setValue(connectionTotalCount + 1, 'totalCount')
    }
  }
}

function addSubIssueToConnectionFromResponse(store: RecordSourceSelectorProxy, input: AddSubIssueInput) {
  const payload = store.getRootField('addSubIssue')
  if (!payload) return

  const subIssueRecord = payload.getLinkedRecord('subIssue')
  if (!subIssueRecord) return

  const parentRecord = store.get(input.issueId)
  if (!parentRecord) return

  const subIssuesConnection = getSubIssuesConnection(parentRecord)
  if (!subIssuesConnection) return

  // Check if the sub-issue is already in the connection (from optimistic update)
  const currentNodes = subIssuesConnection.getLinkedRecords('nodes') || []
  const alreadyExists = currentNodes.some(node => node?.getDataID() === subIssueRecord.getDataID())

  if (!alreadyExists) {
    // Add the sub-issue from the server response to the connection
    subIssuesConnection.setLinkedRecords([...currentNodes, subIssueRecord], 'nodes')

    // Update totalCount on the connection
    const currentTotalCount = subIssuesConnection.getValue('totalCount')
    if (typeof currentTotalCount === 'number') {
      subIssuesConnection.setValue(currentTotalCount + 1, 'totalCount')
    }
  }
}

export function addSubIssueMutation({
  environment,
  input,
  onError,
  onCompleted,
}: {
  environment: Environment
  input: AddSubIssueInput
  onError?: (error: Error) => void
  onCompleted?: (response: addSubIssueMutation$data) => void
}) {
  return commitMutation<addSubIssueMutation>(environment, {
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    mutation: graphql`
      mutation addSubIssueMutation($input: AddSubIssueInput!) @raw_response_type {
        addSubIssue(input: $input) {
          issue {
            id
            subIssuesSummary {
              total
              completed
            }
          }
          subIssue {
            id
            state
            stateReason
            issueDependenciesSummary {
              blockedBy
            }
            assignees(first: 10) {
              totalCount
              edges {
                node {
                  id
                  login
                  avatarUrl
                }
              }
            }
            url
            repository {
              name
              owner {
                login
              }
            }
            databaseId
            number
            title
            titleHTML
            issueType {
              id
              name
              color
            }
            subIssuesSummary {
              total
              completed
            }
            closedByPullRequestsReferences(first: 0, includeClosedPrs: true) {
              totalCount
            }
          }
        }
      }
    `,
    variables: {
      input,
    },
    optimisticUpdater: store => optimisticUpdateSubIssuesConnection(store, input),
    updater: store => addSubIssueToConnectionFromResponse(store, input),
    onError: error => onError && onError(error),
    onCompleted: response => onCompleted && onCompleted(response),
  })
}
